import { Button, Typography } from '@material-ui/core';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import LayersClearIcon from '@material-ui/icons/LayersClear';
import React, { useState } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useNotifyError } from 'utils/notifiers/useNotifyError';

import { useConfigFileLoader } from '../hooks/useConfigFileLoader';
import { updatePartialByIds } from '../services/partialUpdate';
import type { Place } from '../types/warehouse.types';
import { ActionDialog } from './ActionDialog';
import { DialogConfirmActions } from './DialogConfirmActions';
import { SelectedEntitiesList } from './SelectedEntitiesList';
import { useBulkActionStyles } from './styles';

const PLACES_RESOURCE = 'areas';

interface PlaceForSplittedSetClearProps {
  selectedPlaces: Place[];
  terminalId: number;
}

export const PlaceForSplittedSetClear: React.FC<PlaceForSplittedSetClearProps> = ({
  selectedPlaces,
  terminalId,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'set' | 'clear'>('set');
  const [loading, setLoading] = useState(false);
  const classes = useBulkActionStyles();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const { loadConfigFromBackend } = useConfigFileLoader();

  const placesToSet = selectedPlaces.filter((place) => !place.is_for_splitted_pallets);
  const placesToClear = selectedPlaces.filter((place) => place.is_for_splitted_pallets);

  const isClearMode = mode === 'clear';
  const placesToProcess = isClearMode ? placesToClear : placesToSet;
  const entityName = translate(
    placesToProcess.length > 1 ? 'osfc.storage_ui.entity.places' : 'osfc.storage_ui.entity.place'
  );

  const handleOpen = (actionMode: 'set' | 'clear') => {
    setMode(actionMode);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await updatePartialByIds(
        dataProvider,
        PLACES_RESOURCE,
        placesToProcess.map((place) => place.id),
        { is_for_splitted_pallets: !isClearMode }
      );

      notify(
        translate(
          isClearMode ? 'osfc.storage_ui.for_splitted.success_clear' : 'osfc.storage_ui.for_splitted.success_set',
          { count: placesToProcess.length, entities: entityName }
        ),
        { type: 'success' }
      );
      handleClose();
      await loadConfigFromBackend(terminalId);
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={classes.stackedButtonContainer}>
        {placesToSet.length > 0 && (
          <Button
            onClick={() => handleOpen('set')}
            size="small"
            color="primary"
            variant="outlined"
            className={classes.button}
            startIcon={<CallSplitIcon />}
          >
            {translate('osfc.storage_ui.for_splitted.set_button', { count: placesToSet.length })}
          </Button>
        )}

        {placesToClear.length > 0 && (
          <Button
            onClick={() => handleOpen('clear')}
            size="small"
            color="secondary"
            variant="outlined"
            className={classes.button}
            startIcon={<LayersClearIcon />}
          >
            {translate('osfc.storage_ui.for_splitted.clear_button', { count: placesToClear.length })}
          </Button>
        )}
      </div>

      <ActionDialog
        open={open}
        loading={loading}
        title={translate(
          isClearMode ? 'osfc.storage_ui.for_splitted.clear_title' : 'osfc.storage_ui.for_splitted.set_title'
        )}
      >
        <Typography variant="body2" className={classes.confirmText}>
          {translate(
            isClearMode ? 'osfc.storage_ui.for_splitted.confirm_clear' : 'osfc.storage_ui.for_splitted.confirm_set',
            { entities: entityName }
          )}
        </Typography>

        <SelectedEntitiesList
          title={translate('osfc.storage_ui.affected_entities', {
            entities: entityName,
            count: placesToProcess.length,
          })}
          entities={placesToProcess.map((place) => ({ id: place.id, name: place.address }))}
        />

        <DialogConfirmActions
          loading={loading}
          confirmLabel={translate(
            isClearMode ? 'osfc.storage_ui.for_splitted.clear_action' : 'osfc.storage_ui.for_splitted.set_action'
          )}
          onCancel={handleClose}
          onConfirm={handleSubmit}
        />
      </ActionDialog>
    </>
  );
};
